import type { Dayjs } from 'dayjs';
import { isDayjs } from 'dayjs';
import dayjs from 'dayjs';
import { unix } from 'dayjs';
import { raise } from '../error';
import type { Optional } from '../object';
import type { DayjsPeriod } from './date-types';
import type { Period } from './date-types/period';

export function isDateBetween(date: Date, start: Date, end: Date): boolean;
export function isDateBetween(date: Date, period: Period): boolean;

// Implementation
export function isDateBetween(date: Date, startOrPeriod: Date | Period, endTime?: Date): boolean {
  return isDate(startOrPeriod)
    ? date >= startOrPeriod && date <= endTime!
    : isDateBetween(date, startOrPeriod.startTime, startOrPeriod.endTime);
}

export function isDateNotBetween(date: Date, start: Date, end: Date): boolean;
export function isDateNotBetween(date: Date, period: Period): boolean;
export function isDateNotBetween(date: Date, startOrPeriod: Date | Period, endTime?: Date): boolean {
  const isDateBetweenSuccess = isDate(startOrPeriod)
    ? isDateBetween(date, startOrPeriod, endTime!)
    : isDateBetween(date, startOrPeriod);

  return !isDateBetweenSuccess;
}

export function isDate(date: any): date is Date {
  return date instanceof Date;
}

export function arePeriodsOverlapping(period1: Period, period2: Period) {
  return isDateBetween(period1.startTime, period2) || isDateBetween(period1.endTime, period2);
}

export function arePeriodsNotOverlapping(period1: Period, period2: Period) {
  return !arePeriodsOverlapping(period1, period2);
}

export function isDateAfterDate(date1: Date, date2: Date) {
  return date1.getTime() > date2.getTime();
}

export function subtractDays(date: Date, days: number) {
  return dayjs(date).subtract(days, 'day').toDate(); 
}

export function utcFromString(dateStr: Optional<string>) {
  return dateStr ? unix(+dateStr).toDate() : undefined;
}

export function now() {
  return new Date();
}

export function yesterday() {
  return subtractDays(now(), 1);
}

export function getDaysBetween(period: DayjsPeriod): number;
export function getDaysBetween(start: Dayjs, end: Dayjs): number;
export function getDaysBetween(startOrPeriod: Dayjs | DayjsPeriod, end?: Dayjs): number {
  return isDayjs(startOrPeriod)
    ? end!.diff(startOrPeriod, 'days')
    : startOrPeriod.endTime.diff(startOrPeriod.startTime, 'days');
}

export function getTomorrow(date: Dayjs = dayjs()) {
  return date.add(1, 'day');
}

export function buildPeriod(startTime: Date, endTime: Date): Period {
  return {
    startTime,
    endTime,
  };
}

export function removeDateHours(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function getAbbreviatedMonths(): string[] {
  const months = [];

  for (let i = 0; i < 12; i++) {
    const monthAbbreviation = dayjs().month(i).format('MMM');
    months.push(monthAbbreviation);
  }

  return months;
}

export function getPeriodDaysWithUtc(period: Period, utc = false) {
  const daysBetween = getDaysBetween(dayjs(period.startTime).utc(utc), dayjs(period.endTime).utc(utc));

  return Array.from({ length: daysBetween + 1 }).map((_, index) => {
    return dayjs(period.startTime).utc(utc).add(index, 'day');
  });
}

export function getPeriodDays(period: Period) {
  const daysBetween = getDaysBetween(dayjs(period.startTime), dayjs(period.endTime));

  return Array.from({ length: daysBetween + 1 }).map((_, index) => {
    return dayjs(period.startTime).add(index, 'day');
  });
}

export function getPeriodMonths(period: Period) {
  const totalMonths = dayjs(period.endTime).diff(dayjs(period.startTime), 'month');

  return Array.from({ length: totalMonths + 1 }).map((_, index) => {
    return dayjs(period.startTime).add(index, 'month');
  });
}

export function validateDate(date: Dayjs) {
  if (!date.isValid()) {
    raise('Invalid date');
  }
}

export function getOneMonthBefore(date: Date): Date {
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() - 1);

  return newDate;
}

export function buildDayjsPeriod(startTime: Dayjs | Date, endTime: Dayjs | Date): DayjsPeriod {
  return {
    startTime: dayjs(startTime),
    endTime: dayjs(endTime),
  };
}

export function mapDayjsPeriodToPeriod({ startTime, endTime }: DayjsPeriod): Period {
  return {
    startTime: startTime.toDate(),
    endTime: endTime.toDate(),
  };
}

export function mapPeriodToDayjsPeriod({ startTime, endTime }: Period): DayjsPeriod {
  return {
    startTime: dayjs(startTime),
    endTime: dayjs(endTime),
  };
}

export function minimumDate(firstDate: Date, ...dates: Date[]): Date {
  return dates.reduce((workingMinimumDate, currentDate) => 
    (currentDate < workingMinimumDate ? currentDate : workingMinimumDate),
  firstDate);
}

export function periodToString(period: Period) {
  return `${period.startTime.toISOString()}/${period.endTime.toISOString()}`;
}

export function periodFromString(periodString: string): Period {
  const [startTime, endTime] = periodString.split('/').map((date) => new Date(date));

  return {
    startTime,
    endTime,
  };
}

export function getEndOfPreviousDay(date: Date | Dayjs) {
  return dayjs(date).subtract(1, 'day').endOf('day').toDate();
}

export function convertSecondsToMilliseconds(seconds: number) {
  return seconds * 1000;
}

export function getYearPeriod() {
  const startOfYear = dayjs().startOf('year').toDate();
  const endOfYear = dayjs().endOf('year').toDate();

  return buildPeriod(startOfYear, endOfYear);
}

export function getMonthPeriod() {
  const startOfMonth = dayjs().startOf('month').toDate();
  const endOfMonth = dayjs().endOf('month').toDate();

  return buildPeriod(startOfMonth, endOfMonth);
}

export function isInThePast(date: Date) {
  return date < now();
}

export function isInTheFuture(date: Date) {
  return date > now();
}

export function extractDatesFromPeriod(startTimestamp: Date, endTimestamp: Date) {
  const startDate = dayjs(startTimestamp);
  const endDate = dayjs(endTimestamp);

  return {
    startDate: {
      day: startDate.date(),
      month: startDate.month(),
      year: startDate.year(),
    },
    endDate: {
      day: endDate.date(),
      month: endDate.month(),
      year: endDate.year(),
    },
  };
}